import React, { useState, useEffect, useMemo } from 'react';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { 
  XMarkIcon, 
  PlusIcon, 
  TrashIcon,
  UserIcon,
  PhoneIcon,
  IdentificationIcon,
  MapPinIcon,
  CurrencyDollarIcon,
  DocumentTextIcon,
  UsersIcon,
  BuildingStorefrontIcon
} from '@heroicons/react/24/outline';
import apiService from '../../services/api';
import { BookingFormData, Booking, Companion, Trip, Accommodation, Bus, Route } from '../../types';
import { FormField, Input, Select, Textarea, Checkbox, FormCard, DateInput, DateDisplay, MultiRouteForm } from '../../components/Forms';
import { getLocalToday, toGregorianDateOnly } from '../../utils/dateDisplay';
import {
  compareTripsByHailDepartureOldestFirst,
  getTripHailDepartureGregorian,
  tripDepartsFromHail,
} from '../../utils/bookingDeparture';
import { formatHijriLabel } from '../../components/Forms/hijriDate';
import BusSeatSelection from '../../components/BusSeatSelection';

interface BookingFormProps {
  booking?: Booking;
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (booking: Booking) => void;
}

const normalizeBookingRoutes = (routes: Route[]): Route[] =>
  routes.map((route) => ({
    ...route,
    dateTo: route.dateTo || route.dateFrom,
    timeTo: route.timeTo || route.timeFrom,
    distance: 0,
  }));

const buildLegacyRouteFromRoutes = (routes: Route[]): BookingFormData['route'] => {
  if (!routes.length) {
    return {
      from: 'حائل',
      to: 'مكة المكرمة',
      departureDate: '',
      returnDate: '',
    };
  }

  const firstRoute = routes[0];
  const lastRoute = routes[routes.length - 1];
  const activeRoute = routes.find((r) => r.isActive) || firstRoute;

  return {
    from: firstRoute.from,
    to: (routes.length > 1 ? lastRoute.to : activeRoute.to) as BookingFormData['route']['to'],
    departureDate: firstRoute.dateFrom,
    returnDate: routes.length > 1 ? lastRoute.dateFrom : (lastRoute.dateTo || ''),
  };
};

const BookingForm: React.FC<BookingFormProps> = ({ 
  booking, 
  isOpen, 
  onClose, 
  onSuccess 
}) => {
  const queryClient = useQueryClient();
  const isEditing = !!booking;

  // Date helpers to safely handle Date | string | null
  const toDate = (value: unknown): Date | null => {
    if (!value) return null;
    const date = value instanceof Date ? value : new Date(value as any);
    return isNaN(date.getTime()) ? null : date;
    };

  const formatDateOnly = (value: unknown): string => {
    const d = toDate(value);
    return d ? d.toISOString().split('T')[0] : '';
  };

  const formatTimeOnly = (value: unknown): string => {
    const d = toDate(value);
    return d ? d.toISOString().slice(11, 16) : '';
  };

  const [bookingType, setBookingType] = useState<'trip' | 'manual'>('manual');
  const [selectedTrip, setSelectedTrip] = useState<string>('');
  const [selectedBus, setSelectedBus] = useState<string>('');
  const [selectedBoardingStaff, setSelectedBoardingStaff] = useState<string>('');
  const [selectedAccommodation, setSelectedAccommodation] = useState<string>('');
  const [selectedSeats, setSelectedSeats] = useState<{ [passengerIndex: number]: string }>({});
  const [showSeatSelection, setShowSeatSelection] = useState(false);

  const TRIP_STATUS_LABELS: Record<string, string> = {
    draft: 'مسودة',
    published: 'منشورة',
    full: 'مكتملة العدد',
    ongoing: 'جارية',
    completed: 'مكتملة',
    cancelled: 'ملغية',
  };

  const {
    data: tripsData,
    isLoading: tripsLoading,
    isError: tripsError,
    refetch: refetchTrips,
  } = useQuery({
    queryKey: ['trips', 'for-booking'],
    queryFn: () => apiService.getTrips(1, 100, {}),
    enabled: isOpen,
    staleTime: 0,
  });

  const allTrips = tripsData?.data ?? [];

  useEffect(() => {
    if (isOpen && bookingType === 'trip') {
      refetchTrips();
    }
  }, [isOpen, bookingType, refetchTrips]);

  const trips = useMemo(() => {
    const filtered = allTrips.filter((trip) => {
      const status = trip.status || 'draft';
      if (['cancelled', 'completed', 'full'].includes(status)) return false;
      return tripDepartsFromHail(trip);
    });

    return [...filtered].sort(compareTripsByHailDepartureOldestFirst);
  }, [allTrips]);

  // Fetch available buses for manual bookings
  const { data: busesData } = useQuery({
    queryKey: ['buses'],
    queryFn: () => apiService.getBuses(1, 100, {}),
    enabled: bookingType === 'manual'
  });

  // Boarding staff picker for manual bookings
  const { data: boardingStaffList = [] } = useQuery({
    queryKey: ['boarding-staff-for-booking'],
    queryFn: () => apiService.getBoardingStaffForBooking(),
    enabled: isOpen && bookingType === 'manual',
  });

  // Filter buses to show only active ones
  const buses = (busesData?.data || []).filter((bus: Bus) => 
    bus.status === 'available' && bus.isActive
  );

  const boardingStaffOptions = [
    { value: '', label: 'بدون تعيين' },
    ...boardingStaffList.map((u) => ({
      value: u._id,
      label: `${u.fullName || u.username}${u.role === 'operations' ? ' (عمليات)' : ''}`,
    })),
  ];

  const [formData, setFormData] = useState<BookingFormData>({
    tripType: 'umrah',
    routes: [
      {
        from: 'حائل',
        to: 'مكة المكرمة',
        dateFrom: '',
        dateTo: '',
        timeFrom: '',
        timeTo: '',
        distance: 0,
        isActive: true
      }
    ],
    route: {
      from: 'حائل',
      to: 'مكة المكرمة',
      departureDate: '',
      returnDate: ''
    },
    services: {
      transportation: true,
      accommodation: false,
      meals: false,
      guide: false
    },
    contactPerson: {
      name: '',
      idNumber: '',
      nationality: 'سعودي',
      phone: '',
      birthDate: '',
      gender: 'male',
      email: '',
      organization: ''
    },
    companions: [],
    pricing: {
      basePrice: 0,
      transportationFee: 0,
      accommodationFee: 0,
      additionalFees: 0,
      discount: 0,
      totalAmount: 0,
      paidAmount: 0
    },
    paymentMethod: 'cash',
    paymentStatus: 'pending',
    status: 'pending',
    priority: 'normal',
    source: 'walk_in',
    specialRequests: '',
    whatsappReminder: {
      enabled: true,
      hoursBeforeDeparture: 24,
    },
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Fetch available accommodations when accommodation service is selected or when editing with accommodation
  // Uses dashboard endpoint so bookings users can load the list without operations permission
  const { data: accommodations = [] } = useQuery({
    queryKey: ['accommodations', 'for-booking'],
    queryFn: () => apiService.getAccommodationsForBooking(),
    enabled: formData.services.accommodation || (isEditing && !!booking?.assignedAccommodation)
  });

  const selectedTripData = trips.find((t) => t._id === selectedTrip);
  const selectedBusData =
    bookingType === 'trip'
      ? selectedTripData?.bus || selectedTripData?.buses?.[0]?.bus
      : buses.find((b) => b._id === selectedBus);

  // Fetch existing bookings to mark reserved seats (same trip or same bus)
  const seatLookupTripId = bookingType === 'trip' ? selectedTrip : '';
  const seatLookupBusRaw =
    bookingType === 'manual'
      ? selectedBus
      : bookingType === 'trip'
        ? selectedTripData?.buses?.[0]?.bus?._id ||
          selectedTripData?.buses?.[0]?.bus ||
          selectedTripData?.bus?._id ||
          selectedTripData?.bus ||
          selectedBusData?._id ||
          selectedBusData ||
          ''
        : '';
  const seatLookupBusId =
    seatLookupBusRaw && typeof seatLookupBusRaw === 'object'
      ? String((seatLookupBusRaw as { _id?: string })._id || '')
      : String(seatLookupBusRaw || '');

  const { data: existingBookingsData, isFetching: seatsFetching, refetch: refetchSeatBookings } = useQuery({
    queryKey: [
      'bookings',
      'seats',
      bookingType,
      seatLookupTripId || null,
      String(seatLookupBusId || '') || null,
    ],
    queryFn: () =>
      apiService.getBookings(1, 200, {
        ...(seatLookupTripId ? { tripId: seatLookupTripId } : {}),
        ...(seatLookupBusId ? { busId: String(seatLookupBusId) } : {}),
      }),
    enabled:
      !!isOpen &&
      ((bookingType === 'trip' && !!seatLookupTripId) ||
        (bookingType === 'manual' && !!selectedBus)),
    staleTime: 0,
    refetchOnMount: 'always',
  });

  const existingBookings = existingBookingsData?.data || [];

  // Seats already assigned on the trip bus (source of truth after boarding sync)
  const extraReservedSeats = useMemo(() => {
    if (bookingType !== 'trip' || !selectedTripData) return [] as string[];
    const busId = String(
      selectedBusData?._id ||
        selectedTripData.buses?.[0]?.bus?._id ||
        selectedTripData.buses?.[0]?.bus ||
        selectedTripData.bus?._id ||
        selectedTripData.bus ||
        ''
    );
    const seats: string[] = [];
    (selectedTripData.buses || []).forEach((entry: any) => {
      const entryBusId = String(entry?.bus?._id || entry?.bus || '');
      if (busId && entryBusId && entryBusId !== busId) return;
      (entry?.passengers || []).forEach((p: any) => {
        const seat = String(p?.seatNumber || '').trim();
        if (seat && seat !== '—' && seat !== '-' && seat !== '–') {
          seats.push(seat);
        }
      });
    });
    return seats;
  }, [bookingType, selectedTripData, selectedBusData]);

  useEffect(() => {
    if (!showSeatSelection || !isOpen) return;
    refetchSeatBookings();
    if (bookingType === 'trip') refetchTrips();
  }, [showSeatSelection, isOpen, bookingType, refetchSeatBookings, refetchTrips]);

  // Helper function to get trip route info
  const getTripRouteInfo = (trip: Trip) => {
    // If trip has new routes format, use the first active route
    if (trip.routes && trip.routes.length > 0) {
      const activeRoute = trip.routes.find(r => r.isActive) || trip.routes[0];
      return {
        from: activeRoute.from,
        to: activeRoute.to,
        departureDate: activeRoute.dateFrom,
        departureTime: activeRoute.timeFrom,
        arrivalDate: activeRoute.dateTo,
        arrivalTime: activeRoute.timeTo
      };
    }
    
    // Fallback to legacy route format
    if (trip.route) {
      return {
        from: trip.route.from,
        to: trip.route.to,
        departureDate: trip.route.departureTime ? String(trip.route.departureTime).split('T')[0] : '',
        departureTime: trip.route.departureTime ? String(trip.route.departureTime).split('T')[1]?.slice(0, 5) : '',
        arrivalDate: trip.route.arrivalTime ? String(trip.route.arrivalTime).split('T')[0] : '',
        arrivalTime: trip.route.arrivalTime ? String(trip.route.arrivalTime).split('T')[1]?.slice(0, 5) : ''
      };
    }
    
    // Default fallback
    return {
      from: 'غير محدد',
      to: 'غير محدد',
      departureDate: '',
      departureTime: '',
      arrivalDate: '',
      arrivalTime: ''
    };
  };

  // Handle trip selection
  const handleTripSelection = (tripId: string) => {
    setSelectedTrip(tripId);
    const trip = trips.find(t => t._id === tripId);
    if (trip) {
      const routeInfo = getTripRouteInfo(trip);
      
      // Get departure and return dates from route info
      let departureDate = '';
      let returnDate = '';
      
      if (trip.routes && trip.routes.length > 0) {
        const activeRoute = trip.routes.find(r => r.isActive) || trip.routes[0];
        departureDate = activeRoute.dateFrom;
        returnDate = activeRoute.dateTo;
      } else if (trip.route) {
        departureDate = trip.route.departureTime ? String(trip.route.departureTime).split('T')[0] : '';
        returnDate = trip.route.arrivalTime ? String(trip.route.arrivalTime).split('T')[0] : '';
      }
      
      const routes: Route[] = trip.routes?.length
        ? trip.routes
        : [{
            from: routeInfo.from,
            to: routeInfo.to,
            dateFrom: departureDate,
            dateTo: returnDate,
            timeFrom: routeInfo.departureTime,
            timeTo: routeInfo.arrivalTime,
            distance: 0,
            isActive: true,
          }];

      setFormData(prev => ({
        ...prev,
        routes,
        route: buildLegacyRouteFromRoutes(routes),
        services: {
          transportation: true,
          accommodation: false,
          meals: false,
          guide: false
        }
      }));
    }
  };

  // Handle accommodation selection
  const handleAccommodationSelection = (accommodationId: string) => {
    setSelectedAccommodation(accommodationId);
    const accommodation = accommodations.find((a: Accommodation) => a._id === accommodationId);
    if (accommodation) {
      // Update accommodation fee based on selected accommodation
      setFormData(prev => ({
        ...prev,
        pricing: {
          ...prev.pricing,
          accommodationFee: accommodation.contract?.pricePerNight || 0
        }
      }));
    }
  };

  // Handle bus selection for manual bookings
  const handleBusSelection = (busId: string) => {
    setSelectedBus(busId);
  };

  // Initialize form data when editing or reset when creating new
  useEffect(() => {
    if (isEditing && booking) {
      // Manual bookings keep manual UI even when auto-linked to a trip
      const bookingTypeFromData =
        booking.isManual === true || !booking.assignedTrip ? 'manual' : 'trip';
      setBookingType(bookingTypeFromData);
      
      // If booking was created from a trip, set the selected trip
      if (booking.assignedTrip && bookingTypeFromData === 'trip') {
        setSelectedTrip(booking.assignedTrip._id);
      }

      // If booking has accommodation, set the selected accommodation
      if (booking.assignedAccommodation) {
        setSelectedAccommodation(booking.assignedAccommodation._id);
      }

      // If booking has a bus (for manual bookings), set the selected bus
      if (booking.assignedBus && bookingTypeFromData === 'manual') {
        setSelectedBus(booking.assignedBus._id);

        const tripBuses = booking.assignedTrip?.buses || [];
        const busId = booking.assignedBus._id;
        const entry = tripBuses.find((b: any) => {
          const id = b?.bus?._id || b?.bus;
          return id && String(id) === String(busId);
        });
        const staff = entry?.boardingStaff;
        setSelectedBoardingStaff(
          staff
            ? typeof staff === 'object'
              ? (staff as any)._id || ''
              : String(staff)
            : ''
        );
      } else {
        setSelectedBoardingStaff('');
      }

      // Initialize seat selection from existing booking
      const seats: { [passengerIndex: number]: string } = {};
      
      // Contact person seat (if exists)
      if ('seatNumber' in booking.contactPerson && (booking.contactPerson as any).seatNumber) {
        seats[0] = (booking.contactPerson as any).seatNumber;
      }

      // Companions seats
      booking.companions?.forEach((companion, index) => {
        if (companion.seatNumber) {
          seats[index + 1] = companion.seatNumber;
        }
      });

      setSelectedSeats(seats);
      
      // Helper function for booking route info
      const getBookingRouteInfo = () => {
        // If booking has new routes format, use the first active route
        if (booking.routes && booking.routes.length > 0) {
          const activeRoute = booking.routes.find(r => r.isActive) || booking.routes[0];
          const dateFrom = activeRoute.dateFrom || '';
          const dateTo = activeRoute.dateTo || activeRoute.dateFrom || '';
          const timeFrom = activeRoute.timeFrom || '00:00';
          const timeTo = activeRoute.timeTo || activeRoute.timeFrom || '00:00';
          return {
            from: activeRoute.from,
            to: activeRoute.to,
            departureDate: dateFrom ? toDate(`${dateFrom}T${timeFrom}`) : null,
            returnDate: dateTo ? toDate(`${dateTo}T${timeTo}`) : null,
          };
        }
        
        // Fallback to legacy route format
        if (booking.route) {
          return {
            from: booking.route.from,
            to: booking.route.to,
            departureDate: toDate(booking.route.departureDate),
            returnDate: toDate(booking.route.returnDate)
          };
        }
        
        // Default fallback
        return {
          from: 'حائل',
          to: 'مكة المكرمة' as 'مكة المكرمة' | 'المدينة المنورة',
          departureDate: new Date(),
          returnDate: new Date()
        };
      };

      const routeInfo = getBookingRouteInfo();

      const loadedRoutes =
        booking.routes && booking.routes.length > 0
          ? normalizeBookingRoutes(
              booking.routes.map((route) => ({
                from: route.from || '',
                to: route.to || '',
                dateFrom: toGregorianDateOnly(route.dateFrom) || route.dateFrom || '',
                dateTo:
                  toGregorianDateOnly(route.dateTo) ||
                  route.dateTo ||
                  toGregorianDateOnly(route.dateFrom) ||
                  route.dateFrom ||
                  '',
                timeFrom: route.timeFrom || '',
                timeTo: route.timeTo || route.timeFrom || '',
                distance: route.distance ?? 0,
                isActive: route.isActive !== false,
              }))
            )
          : [
              {
                from: routeInfo.from,
                to: routeInfo.to,
                dateFrom: formatDateOnly(routeInfo.departureDate),
                dateTo: formatDateOnly(routeInfo.returnDate),
                timeFrom: formatTimeOnly(routeInfo.departureDate),
                timeTo: formatTimeOnly(routeInfo.returnDate),
                distance: 0,
                isActive: true,
              },
            ];

      setFormData({
        tripType: booking.tripType,
        routes: loadedRoutes,
        route: buildLegacyRouteFromRoutes(loadedRoutes),
        services: booking.services,
        contactPerson: {
          name: booking.contactPerson.name,
          idNumber: booking.contactPerson.idNumber,
          nationality: booking.contactPerson.nationality,
          phone: booking.contactPerson.phone,
          birthDate: toGregorianDateOnly(booking.contactPerson.birthDate) || formatDateOnly(booking.contactPerson.birthDate),
          gender: booking.contactPerson.gender,
          email: booking.contactPerson.email || '',
          organization: booking.contactPerson.organization || ''
        },
        companions: booking.companions?.map(companion => ({
          name: companion.name,
          idNumber: companion.idNumber,
          nationality: companion.nationality,
          phone: companion.phone || '',
          birthDate: toGregorianDateOnly(companion.birthDate) || formatDateOnly(companion.birthDate),
          age: companion.age,
          gender: companion.gender,
          seatNumber: companion.seatNumber
        })) || [],
        pricing: {
          basePrice: booking.pricing.basePrice,
          transportationFee: booking.pricing.transportationFee,
          accommodationFee: booking.pricing.accommodationFee,
          additionalFees: booking.pricing.additionalFees,
          discount: booking.pricing.discount,
          totalAmount: booking.pricing.totalAmount,
          paidAmount: booking.pricing.paidAmount
        },
        paymentMethod: booking.paymentMethod,
        paymentStatus: booking.paymentStatus,
        status: booking.status,
        priority: booking.priority || 'normal',
        source: booking.source || 'walk_in',
        specialRequests: booking.specialRequests || '',
        whatsappReminder: {
          enabled: booking.whatsappReminder?.enabled !== false,
          hoursBeforeDeparture: booking.whatsappReminder?.hoursBeforeDeparture || 24,
        },
      });
    } else if (!isEditing) {
      // Reset form for new booking
      resetForm();
      setBookingType('manual');
      setSelectedTrip('');
      setSelectedAccommodation('');
    }
  }, [isEditing, booking]);

  // Calculate total amount when pricing fields change
  useEffect(() => {
    const total = formData.pricing.basePrice + 
                  formData.pricing.transportationFee + 
                  formData.pricing.accommodationFee + 
                  formData.pricing.additionalFees - 
                  formData.pricing.discount;
    
    setFormData(prev => ({
      ...prev,
      pricing: {
        ...prev.pricing,
        totalAmount: Math.max(0, total)
      }
    }));
  }, [
    formData.pricing.basePrice,
    formData.pricing.transportationFee,
    formData.pricing.accommodationFee,
    formData.pricing.additionalFees,
    formData.pricing.discount
  ]);

  // Create/Update mutations
  const createMutation = useMutation({
    mutationFn: (data: BookingFormData) => apiService.createBooking(data),
    onSuccess: (newBooking) => {
      queryClient.invalidateQueries({ queryKey: ['bookings'] });
      queryClient.invalidateQueries({ queryKey: ['trips'] });
      queryClient.invalidateQueries({ queryKey: ['boarding-trips'] });
      queryClient.invalidateQueries({ queryKey: ['boarding-buses'] });
      queryClient.invalidateQueries({ queryKey: ['boarding-manifest'] });
      onSuccess?.(newBooking);
      onClose();
      resetForm();
    },
    onError: (error: any) => {
      console.error('Create booking error:', error);
      setErrors({
        submit:
          error?.response?.data?.message ||
          error?.message ||
          'حدث خطأ أثناء إنشاء الحجز',
      });
    }
  });

  const updateMutation = useMutation({
    mutationFn: (data: BookingFormData) => apiService.updateBooking(booking!._id, data),
    onSuccess: (updatedBooking) => {
      queryClient.invalidateQueries({ queryKey: ['bookings'] });
      queryClient.invalidateQueries({ queryKey: ['trips'] });
      queryClient.invalidateQueries({ queryKey: ['boarding-trips'] });
      queryClient.invalidateQueries({ queryKey: ['boarding-buses'] });
      queryClient.invalidateQueries({ queryKey: ['boarding-manifest'] });
      onSuccess?.(updatedBooking);
      onClose();
    },
    onError: (error: any) => {
      console.error('Update booking error:', error);
      setErrors({
        submit:
          error?.response?.data?.message ||
          error?.message ||
          'حدث خطأ أثناء تحديث الحجز',
      });
    }
  });

  const resetForm = () => {
    setFormData({
      tripType: 'umrah',
      routes: [
        {
          from: 'حائل',
          to: 'مكة المكرمة',
          dateFrom: '',
          dateTo: '',
          timeFrom: '',
          timeTo: '',
          distance: 0,
          isActive: true
        }
      ],
      route: {
        from: 'حائل',
        to: 'مكة المكرمة',
        departureDate: '',
        returnDate: ''
      },
      services: {
        transportation: true,
        accommodation: false,
        meals: false,
        guide: false
      },
      contactPerson: {
        name: '',
        idNumber: '',
        nationality: 'سعودي',
        phone: '',
        birthDate: '',
        gender: 'male',
        email: '',
        organization: ''
      },
      companions: [],
      pricing: {
        basePrice: 0,
        transportationFee: 0,
        accommodationFee: 0,
        additionalFees: 0,
        discount: 0,
        totalAmount: 0,
        paidAmount: 0
      },
      paymentMethod: 'cash',
      paymentStatus: 'pending',
      status: 'pending',
      priority: 'normal',
      source: 'walk_in',
      specialRequests: '',
      whatsappReminder: {
        enabled: true,
        hoursBeforeDeparture: 24,
      },
    });
    setSelectedTrip('');
    setSelectedBus('');
    setSelectedBoardingStaff('');
    setSelectedAccommodation('');
    setSelectedSeats({});
    setShowSeatSelection(false);
    setErrors({});
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    const today = getLocalToday();

    // Routes validation
    if (bookingType === 'manual') {
      if (!formData.routes || formData.routes.length === 0) {
        newErrors.routes = 'يجب إضافة مسار واحد على الأقل';
      } else {
        // Validate each route
        formData.routes.forEach((route, index) => {
          if (!route.from || !route.from.trim()) {
            newErrors[`routes.${index}.from`] = 'مدينة المغادرة مطلوبة';
          }
          if (!route.to || !route.to.trim()) {
            newErrors[`routes.${index}.to`] = 'مدينة الوصول مطلوبة';
          }
          if (!route.dateFrom || !route.dateFrom.trim()) {
            newErrors[`routes.${index}.dateFrom`] = 'التاريخ مطلوب';
          } else if (!isEditing && route.dateFrom < today) {
            newErrors[`routes.${index}.dateFrom`] = 'تاريخ المغادرة لا يمكن أن يكون في الماضي';
          }
          if (!route.timeFrom || !route.timeFrom.trim()) {
            newErrors[`routes.${index}.timeFrom`] = 'وقت المغادرة مطلوب';
          }
          if (route.from && route.to && route.from === route.to) {
            newErrors[`routes.${index}.to`] = 'مدينة الوصول يجب أن تكون مختلفة عن مدينة المغادرة';
          }
        });

        if (formData.routes.length > 1) {
          const first = formData.routes[0];
          const last = formData.routes[formData.routes.length - 1];
          const lastIndex = formData.routes.length - 1;
          if (first.dateFrom && last.dateFrom && first.dateFrom > last.dateFrom) {
            newErrors[`routes.${lastIndex}.dateFrom`] = 'تاريخ العودة لا يمكن أن يكون قبل تاريخ المغادرة';
          }
        }
      }
    }

    // Legacy route validation for trip bookings
    if (bookingType === 'trip') {
      if (!selectedTrip) {
        newErrors.selectedTrip = 'يجب اختيار رحلة';
      }

      const legacyRoute = buildLegacyRouteFromRoutes(formData.routes);

      if (!legacyRoute.departureDate) {
        newErrors['route.departureDate'] = 'تاريخ المغادرة مطلوب';
      } else if (!isEditing && legacyRoute.departureDate < today) {
        newErrors['route.departureDate'] = 'تاريخ المغادرة لا يمكن أن يكون في الماضي';
      }
      if (!legacyRoute.returnDate) {
        newErrors['route.returnDate'] =
          'تاريخ العودة غير متوفر في الرحلة — أضف مسار العودة أو تاريخ النهاية من صفحة الرحلات';
      } else if (
        legacyRoute.departureDate &&
        legacyRoute.returnDate &&
        legacyRoute.departureDate > legacyRoute.returnDate
      ) {
        newErrors['route.returnDate'] = 'تاريخ العودة لا يمكن أن يكون قبل تاريخ المغادرة';
      }
    }

    // Contact person validation
    if (!formData.contactPerson.name) {
      newErrors['contactPerson.name'] = 'اسم الشخص المسؤول مطلوب';
    }
    if (!formData.contactPerson.idNumber) {
      newErrors['contactPerson.idNumber'] = 'رقم الهوية مطلوب';
    }
    if (!formData.contactPerson.phone) {
      newErrors['contactPerson.phone'] = 'رقم الجوال مطلوب';
    }
    if (!formData.contactPerson.birthDate) {
      newErrors['contactPerson.birthDate'] = 'تاريخ الميلاد مطلوب';
    }
    if (!formData.contactPerson.nationality) {
      newErrors['contactPerson.nationality'] = 'الجنسية مطلوبة';
    }

    // Validate companions
    formData.companions.forEach((companion, index) => {
      if (!companion.name) {
        newErrors[`companions.${index}.name`] = 'اسم المرافق مطلوب';
      }
      if (!companion.idNumber) {
        newErrors[`companions.${index}.idNumber`] = 'رقم الهوية مطلوب';
      }
      if (!companion.birthDate) {
        newErrors[`companions.${index}.birthDate`] = 'تاريخ الميلاد مطلوب';
      }
    });

    if (formData.pricing.totalAmount <= 0) {
      newErrors['pricing.totalAmount'] = 'المبلغ الإجمالي يجب أن يكون أكبر من صفر';
    }

    if (bookingType === 'manual' && formData.services.transportation) {
      if (!selectedBus) {
        newErrors.selectedBus = 'يجب اختيار الحافلة';
      }
      if (!selectedBoardingStaff) {
        newErrors.boardingStaff = 'يجب تعيين موظف بوابة الصعود';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Form submitted, validating...', { bookingType, formData });
    
    if (!validateForm()) {
      return;
    }

    console.log('Validation passed, preparing submission data...');

    // Prepare submission data with trip, bus, and accommodation if selected
    // Always fill dateTo/timeTo so update validation accepts multi-route bookings
    const routesForSubmit = normalizeBookingRoutes(formData.routes);

    const submissionData = {
      ...formData,
      routes: routesForSubmit,
      route: buildLegacyRouteFromRoutes(routesForSubmit),
      isManual: bookingType === 'manual',
      selectedTrip: bookingType === 'trip' && selectedTrip ? selectedTrip : undefined,
      assignedTrip: bookingType === 'trip' && selectedTrip ? selectedTrip : undefined,
      assignedBus:
        bookingType === 'manual' && selectedBus
          ? selectedBus
          : bookingType === 'trip' && selectedTripData
            ? (selectedTripData.buses?.[0]?.bus?._id ||
                selectedTripData.buses?.[0]?.bus ||
                selectedTripData.bus?._id ||
                selectedTripData.bus ||
                undefined)
            : undefined,
      boardingStaff:
        bookingType === 'manual' ? selectedBoardingStaff || null : undefined,
      assignedAccommodation: formData.services.accommodation && selectedAccommodation ? selectedAccommodation : undefined
    };

    console.log('Submitting data:', submissionData);

    if (isEditing) {
      updateMutation.mutate(submissionData);
    } else {
      createMutation.mutate(submissionData);
    }
  };

  const addCompanion = () => {
    setFormData(prev => ({
      ...prev,
      companions: [
        ...prev.companions,
        {
          name: '',
          idNumber: '',
          nationality: 'سعودي',
          phone: '',
          birthDate: '',
          age: undefined,
          gender: 'male',
          seatNumber: ''
        }
      ]
    }));
  };

  const removeCompanion = (index: number) => {
    setFormData(prev => ({
      ...prev,
      companions: prev.companions.filter((_, i) => i !== index)
    }));
  };

  const updateCompanion = (index: number, field: keyof Companion, value: any) => {
    setFormData(prev => ({
      ...prev,
      companions: prev.companions.map((companion, i) => 
        i === index ? { ...companion, [field]: value } : companion
      )
    }));
  };

  // Handle seat selection
  const handleSeatSelect = (passengerIndex: number, seatNumber: string) => {
    setSelectedSeats(prev => ({
      ...prev,
      [passengerIndex]: seatNumber
    }));

    // Update the companion's seat number in form data
    if (passengerIndex === 0) {
      // This is the contact person (index 0)
      setFormData(prev => ({
        ...prev,
        contactPerson: {
          ...prev.contactPerson,
          seatNumber: seatNumber
        } as any
      }));
    } else {
      // This is a companion (index - 1)
      const companionIndex = passengerIndex - 1;
      updateCompanion(companionIndex, 'seatNumber', seatNumber);
    }
  };

  // Get all passengers (contact person + companions) for seat selection
  const getAllPassengers = () => {
    const passengers = [
      {
        name: formData.contactPerson.name,
        isMainContact: true
      }
    ];

    formData.companions.forEach(companion => {
      passengers.push({
        name: companion.name,
        isMainContact: false
      });
    });

    return passengers;
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'SAR',
      minimumFractionDigits: 0
    }).format(amount).replace('SAR', 'ر.س');
  };

  // Options for select components
  const tripTypeOptions = [
    { value: 'hajj', label: 'حج' },
    { value: 'umrah', label: 'عمرة' },
    { value: 'transport', label: 'نقل' }
  ];

  const destinationOptions = [
    { value: 'مكة المكرمة', label: 'مكة المكرمة' },
    { value: 'المدينة المنورة', label: 'المدينة المنورة' }
  ];

  const genderOptions = [
    { value: 'male', label: 'ذكر' },
    { value: 'female', label: 'أنثى' }
  ];

  const paymentMethodOptions = [
    { value: 'cash', label: 'نقدي' },
    { value: 'card', label: 'بطاقة' },
    { value: 'transfer', label: 'تحويل' },
    { value: 'partial', label: 'مختلط' }
  ];

  const paymentStatusOptions = [
    { value: 'pending', label: 'في انتظار الدفع' },
    { value: 'partial', label: 'مدفوع جزئياً' },
    { value: 'paid', label: 'مدفوع بالكامل' },
    { value: 'refunded', label: 'مسترد' }
  ];

  const statusOptions = [
    { value: 'pending', label: 'في انتظار التأكيد' },
    { value: 'confirmed', label: 'مؤكد' },
    { value: 'cancelled', label: 'ملغي' },
    { value: 'completed', label: 'مكتمل' },
    { value: 'refunded', label: 'مسترد' }
  ];

  const priorityOptions = [
    { value: 'low', label: 'منخفضة' },
    { value: 'normal', label: 'عادية' },
    { value: 'high', label: 'عالية' },
    { value: 'urgent', label: 'عاجل' }
  ];

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={onClose}></div>

        <div className="inline-block w-full max-w-4xl p-6 my-8 overflow-hidden text-right align-middle transition-all transform bg-white shadow-xl rounded-lg">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900">
              {isEditing ? 'تعديل الحجز' : 'حجز جديد'}
            </h2>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Booking Type Selection */}
            <FormCard 
              title="نوع الحجز" 
              icon={<MapPinIcon className="w-5 h-5 text-primary-600" />}
            >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div 
                    className={`cursor-pointer rounded-lg border-2 p-4 transition-colors ${
                      bookingType === 'trip' 
                        ? 'border-primary-500 bg-primary-50' 
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                    onClick={() => setBookingType('trip')}
                  >
                    <div className="flex items-center">
                      <input
                        type="radio"
                        name="bookingType"
                        value="trip"
                        checked={bookingType === 'trip'}
                        onChange={() => setBookingType('trip')}
                        className="ml-3"
                      />
                      <div>
                        <h3 className="font-medium text-gray-900">حجز برحلة موجودة</h3>
                        <p className="text-sm text-gray-600">اختر من الرحلات المتاحة</p>
                      </div>
                    </div>
                  </div>

                  <div 
                    className={`cursor-pointer rounded-lg border-2 p-4 transition-colors ${
                      bookingType === 'manual' 
                        ? 'border-primary-500 bg-primary-50' 
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                    onClick={() => setBookingType('manual')}
                  >
                    <div className="flex items-center">
                      <input
                        type="radio"
                        name="bookingType"
                        value="manual"
                        checked={bookingType === 'manual'}
                        onChange={() => setBookingType('manual')}
                        className="ml-3"
                      />
                      <div>
                        <h3 className="font-medium text-gray-900">حجز يدوي</h3>
                        <p className="text-sm text-gray-600">إنشاء حجز بتفاصيل مخصصة</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Trip Selection */}
                {bookingType === 'trip' && (
                  <div className="mt-4">
                    <FormField label="اختر الرحلة" required>
                      <Select
                        options={trips.map((trip) => {
                          const routeInfo = getTripRouteInfo(trip);
                          const statusLabel = TRIP_STATUS_LABELS[trip.status] || trip.status;
                          const hailDeparture = getTripHailDepartureGregorian(trip);
                          const hijriDate = hailDeparture ? formatHijriLabel(hailDeparture) : '';
                          const datePart = hijriDate ? ` (${hijriDate})` : '';
                          return {
                            value: trip._id,
                            label: `${trip.tripNumber} — ${routeInfo.from} → ${routeInfo.to}${datePart} [${statusLabel}]`,
                          };
                        })}
                        value={selectedTrip}
                        onChange={(e) => handleTripSelection(e.target.value)}
                        placeholder={
                          tripsLoading
                            ? 'جاري تحميل الرحلات...'
                            : trips.length === 0
                              ? 'لا توجد رحلات متاحة للحجز'
                              : 'اختر رحلة'
                        }
                        disabled={tripsLoading || trips.length === 0}
                      />
                    </FormField>
                    {tripsError && (
                      <p className="text-red-600 text-sm mt-2">
                        تعذّر تحميل الرحلات.{' '}
                        <button
                          type="button"
                          onClick={() => refetchTrips()}
                          className="text-primary-600 underline"
                        >
                          إعادة المحاولة
                        </button>
                      </p>
                    )}
                    {!tripsLoading && !tripsError && allTrips.length > 0 && trips.length === 0 && (
                      <p className="text-amber-700 text-sm mt-2">
                        لديك {allTrips.length} رحلة، لكن لا توجد حالياً متاحة للحجز
                        (تأكد أن الرحلة من حائل وحالتها «مسودة» أو «منشورة» أو «جارية»).
                      </p>
                    )}
                    {!tripsLoading && !tripsError && allTrips.length === 0 && (
                      <p className="text-gray-600 text-sm mt-2">
                        لا توجد رحلات. أضف رحلة من قائمة «الرحلات» ثم ارجع للحجز.
                      </p>
                    )}
                    {errors.selectedTrip && (
                      <p className="text-red-600 text-sm mt-2">{errors.selectedTrip}</p>
                    )}
                    {(errors['route.departureDate'] || errors['route.returnDate']) && (
                      <div className="bg-red-50 border border-red-200 rounded-lg p-3 mt-2 space-y-1">
                        {errors['route.departureDate'] && (
                          <p className="text-red-600 text-sm">{errors['route.departureDate']}</p>
                        )}
                        {errors['route.returnDate'] && (
                          <p className="text-red-600 text-sm">{errors['route.returnDate']}</p>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </FormCard>
            

            {/* Trip Information */}
            <FormCard 
              title="معلومات الرحلة" 
              icon={<MapPinIcon className="w-5 h-5 text-primary-600" />}
            >
              <div className="mb-4">
                <FormField label="نوع الرحلة" required>
                  <Select
                    options={tripTypeOptions}
                    value={formData.tripType}
                    onChange={(e) => setFormData(prev => ({ ...prev, tripType: e.target.value as any }))}
                    disabled={bookingType === 'trip' && !!selectedTrip}
                  />
                </FormField>
              </div>

              {/* Show selected trip routes if booking from trip */}
              {bookingType === 'trip' && selectedTrip && (
                <div className="mb-4 p-4 bg-blue-50 rounded-lg">
                  <h4 className="font-medium text-blue-900 mb-3">مسارات الرحلة المحددة</h4>
                  {(() => {
                    const trip = trips.find(t => t._id === selectedTrip);
                    if (!trip) return null;
                    
                    return trip.routes && trip.routes.length > 0 ? (
                      <div className="space-y-3">
                        {trip.routes.map((route, index) => (
                          <div key={index} className={`p-3 rounded border ${route.isActive ? 'border-green-300 bg-green-50' : 'border-gray-200 bg-gray-50'}`}>
                            <div className="flex items-center justify-between mb-2">
                              <span className="font-medium">المسار {index + 1}</span>
                              {route.isActive && (
                                <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">نشط</span>
                              )}
                            </div>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                              <div><span className="text-gray-600">من:</span> {route.from}</div>
                              <div><span className="text-gray-600">إلى:</span> {route.to}</div>
                              <div>
                                <span className="text-gray-600">المغادرة:</span>{' '}
                                <DateDisplay value={route.dateFrom} dualLayout="inline" className="inline-flex" />
                                {route.timeFrom ? ` - ${route.timeFrom}` : ''}
                              </div>
                              <div>
                                <span className="text-gray-600">الوصول:</span>{' '}
                                <DateDisplay value={route.dateTo} dualLayout="inline" className="inline-flex" />
                                {route.timeTo ? ` - ${route.timeTo}` : ''}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-600">لا توجد مسارات محددة لهذه الرحلة</p>
                    );
                  })()}
                </div>
              )}

              {/* Multi-Route Form for manual bookings */}
              {bookingType === 'manual' && (
                <div>
                  <h4 className="font-medium text-gray-900 mb-3">مسارات الحجز</h4>
                  <MultiRouteForm
                    variant="booking"
                    routes={formData.routes}
                    onRoutesChange={(routes) =>
                      setFormData((prev) => ({
                        ...prev,
                        routes,
                        route: buildLegacyRouteFromRoutes(routes),
                      }))
                    }
                    errors={errors}
                  />
                </div>
              )}

              {/* Services */}
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  الخدمات المطلوبة
                </label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <Checkbox
                    label="النقل"
                    checked={formData.services.transportation}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      services: { ...prev.services, transportation: e.target.checked }
                    }))}
                  />
                  <Checkbox
                    label="الإقامة"
                    checked={formData.services.accommodation}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      services: { ...prev.services, accommodation: e.target.checked }
                    }))}
                  />
                  <Checkbox
                    label="الوجبات"
                    checked={formData.services.meals}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      services: { ...prev.services, meals: e.target.checked }
                    }))}
                  />
                  <Checkbox
                    label="المرشد"
                    checked={formData.services.guide}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      services: { ...prev.services, guide: e.target.checked }
                    }))}
                  />
                </div>
              </div>

              {/* Accommodation Selection */}
              {formData.services.accommodation && (
                <div className="mt-4">
                  <FormField label="اختيار السكن" required>
                    <Select
                      value={selectedAccommodation}
                      onChange={(e) => handleAccommodationSelection(e.target.value)}
                      options={[
                        { value: '', label: 'اختر السكن' },
                        ...accommodations.map((accommodation: Accommodation) => ({
                          value: accommodation._id,
                          label: `${accommodation.name} - ${accommodation.location} (${accommodation.contract?.pricePerNight || 0} ر.س/ليلة)`
                        }))
                      ]}
                    />
                  </FormField>
                  
                  {accommodations.length === 0 && (
                    <p className="text-sm text-gray-500 mt-1">
                      لا توجد أماكن سكن متاحة حالياً
                    </p>
                  )}
                </div>
              )}
            </FormCard>

            {/* Contact Person */}
            <FormCard 
              title="بيانات الشخص المسؤول" 
              icon={<UserIcon className="w-5 h-5 text-primary-600" />}
            >
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <FormField label="الاسم الكامل" required error={errors['contactPerson.name']}>
                  <Input
                    error={!!errors['contactPerson.name']}
                    value={formData.contactPerson.name}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      contactPerson: { ...prev.contactPerson, name: e.target.value }
                    }))}
                  />
                </FormField>

                <FormField label="رقم الهوية" required error={errors['contactPerson.idNumber']}>
                  <Input
                    error={!!errors['contactPerson.idNumber']}
                    value={formData.contactPerson.idNumber}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      contactPerson: { ...prev.contactPerson, idNumber: e.target.value }
                    }))}
                  />
                </FormField>

                <FormField label="الجنسية" required error={errors['contactPerson.nationality']}>
                  <Input
                    error={!!errors['contactPerson.nationality']}
                    value={formData.contactPerson.nationality}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      contactPerson: { ...prev.contactPerson, nationality: e.target.value }
                    }))}
                  />
                </FormField>

                <FormField label="رقم الجوال" required error={errors['contactPerson.phone']}>
                  <Input
                    type="tel"
                    error={!!errors['contactPerson.phone']}
                    value={formData.contactPerson.phone}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      contactPerson: { ...prev.contactPerson, phone: e.target.value }
                    }))}
                  />
                </FormField>

                <FormField error={errors['contactPerson.birthDate']}>
                  <DateInput
                    label={
                      <span>
                        تاريخ الميلاد <span className="text-red-500">*</span>
                      </span>
                    }
                    error={!!errors['contactPerson.birthDate']}
                    value={formData.contactPerson.birthDate}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      contactPerson: { ...prev.contactPerson, birthDate: e.target.value }
                    }))}
                  />
                </FormField>

                <FormField label="الجنس" required>
                  <Select
                    options={genderOptions}
                    value={formData.contactPerson.gender}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      contactPerson: { ...prev.contactPerson, gender: e.target.value as any }
                    }))}
                  />
                </FormField>

                <FormField label="البريد الإلكتروني">
                  <Input
                    type="email"
                    value={formData.contactPerson.email}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      contactPerson: { ...prev.contactPerson, email: e.target.value }
                    }))}
                  />
                </FormField>

                <FormField label="المؤسسة / الشركة (اختياري)">
                  <Input
                    placeholder="اسم المؤسسة أو الشركة"
                    value={formData.contactPerson.organization}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      contactPerson: { ...prev.contactPerson, organization: e.target.value }
                    }))}
                  />
                </FormField>
              </div>
            </FormCard>

            {/* Companions */}
            <FormCard 
              title="المرافقون (اختياري)" 
              icon={<UsersIcon className="w-5 h-5 text-primary-600" />}
            >
              <div className="flex items-center justify-between mb-4">
                <p className="text-sm text-gray-600">
                  يمكن إضافة المرافقين للشخص المسؤول (اختياري)
                </p>
                <button
                  type="button"
                  onClick={addCompanion}
                  className="btn-secondary text-sm"
                >
                  <PlusIcon className="w-4 h-4 ml-1" />
                  إضافة مرافق
                </button>
              </div>

              {formData.companions.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <UsersIcon className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                  <p>لا توجد مرافقون مضافون</p>
                  <p className="text-sm">يمكنك إضافة المرافقين باستخدام الزر أعلاه</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {formData.companions.map((companion, index) => (
                    <div key={index} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-medium text-gray-900">
                          مرافق {index + 1}
                        </h4>
                        <button
                          type="button"
                          onClick={() => removeCompanion(index)}
                          className="text-red-600 hover:text-red-800"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        <FormField label="الاسم الكامل" required error={errors[`companions.${index}.name`]}>
                          <Input
                            error={!!errors[`companions.${index}.name`]}
                            value={companion.name}
                            onChange={(e) => updateCompanion(index, 'name', e.target.value)}
                          />
                        </FormField>

                        <FormField label="رقم الهوية" required error={errors[`companions.${index}.idNumber`]}>
                          <Input
                            error={!!errors[`companions.${index}.idNumber`]}
                            value={companion.idNumber}
                            onChange={(e) => updateCompanion(index, 'idNumber', e.target.value)}
                          />
                        </FormField>

                        <FormField label="الجنسية">
                          <Input
                            value={companion.nationality}
                            onChange={(e) => updateCompanion(index, 'nationality', e.target.value)}
                          />
                        </FormField>

                        <FormField label="رقم الجوال">
                          <Input
                            type="tel"
                            value={companion.phone}
                            onChange={(e) => updateCompanion(index, 'phone', e.target.value)}
                          />
                        </FormField>

                        <FormField error={errors[`companions.${index}.birthDate`]}>
                          <DateInput
                            label={
                              <span>
                                تاريخ الميلاد <span className="text-red-500">*</span>
                              </span>
                            }
                            error={!!errors[`companions.${index}.birthDate`]}
                            value={companion.birthDate}
                            onChange={(e) => updateCompanion(index, 'birthDate', e.target.value)}
                          />
                        </FormField>

                        <FormField label="الجنس" required>
                          <Select
                            options={genderOptions}
                            value={companion.gender}
                            onChange={(e) => updateCompanion(index, 'gender', e.target.value)}
                          />
                        </FormField>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </FormCard>

            {/* Bus Selection for Manual Bookings */}
            {bookingType === 'manual' && formData.services.transportation && (
              <FormCard 
                title="اختيار الحافلة" 
                icon={<BuildingStorefrontIcon className="w-5 h-5 text-primary-600" />}
              >
                <FormField label="اختر الحافلة" required error={errors.selectedBus}>
                  <Select
                    options={buses.map(bus => ({
                      value: bus._id,
                      label: `${bus.busNumber} - ${bus.model?.brand || ''} ${bus.model?.model || ''} (${bus.capacity} مقعد)`
                    }))}
                    value={selectedBus}
                    onChange={(e) => handleBusSelection(e.target.value)}
                    placeholder="اختر حافلة"
                  />
                </FormField>
                
                {buses.length === 0 && (
                  <p className="text-sm text-gray-500 mt-1">
                    لا توجد حافلات متاحة حالياً
                  </p>
                )}

                <FormField
                  label="موظف بوابة الصعود"
                  required
                  error={errors.boardingStaff}
                  className="mt-4"
                >
                  <Select
                    options={boardingStaffOptions}
                    value={selectedBoardingStaff}
                    onChange={(e) => setSelectedBoardingStaff(e.target.value)}
                    placeholder="اختر موظف الصعود"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    يُربط الحجز برحلة تلقائياً ويظهر الباص لهذا الموظف في بوابة الصعود. يمكن تعديل التعيين لاحقاً.
                  </p>
                </FormField>
              </FormCard>
            )}

            {/* Seat Selection */}
            {((bookingType === 'trip' && selectedTripData) || (bookingType === 'manual' && selectedBus)) && formData.services.transportation && (
              <FormCard 
                title="اختيار المقاعد" 
                icon={<UsersIcon className="w-5 h-5 text-primary-600" />}
              >
                <div className="space-y-4">
                  {getAllPassengers().length > 0 ? (
                    <>
                      <div className="flex items-center justify-between">
                        <p className="text-sm text-gray-600">
                          اختر المقاعد لجميع المسافرين في الحافلة
                        </p>
                        <button
                          type="button"
                          onClick={() => setShowSeatSelection(!showSeatSelection)}
                          className="btn-secondary text-sm"
                        >
                          {showSeatSelection ? 'إخفاء المقاعد' : 'عرض المقاعد'}
                        </button>
                      </div>

                      {showSeatSelection && selectedBusData && (
                        <BusSeatSelection
                          bus={selectedBusData}
                          existingBookings={existingBookings}
                          extraReservedSeats={extraReservedSeats}
                          excludeBookingId={isEditing ? booking?._id : undefined}
                          selectedSeats={selectedSeats}
                          onSeatSelect={handleSeatSelect}
                          passengers={getAllPassengers()}
                          isLoadingReserved={seatsFetching}
                        />
                      )}

                      {!showSeatSelection && (
                        <div className="bg-gray-50 rounded-lg p-4">
                          <h4 className="font-medium text-gray-900 mb-3">المقاعد المحددة:</h4>
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                            {getAllPassengers().map((passenger, index) => (
                              <div key={index} className="flex items-center justify-between bg-white rounded-lg p-3 border">
                                <span className="text-sm font-medium text-gray-900">
                                  {passenger.name}
                                  {passenger.isMainContact && (
                                    <span className="mr-2 bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded">
                                      المسؤول
                                    </span>
                                  )}
                                </span>
                                <span className="text-sm text-gray-600">
                                  {selectedSeats[index] ? (
                                    <span className="bg-green-100 text-green-800 px-2 py-1 rounded">
                                      مقعد {selectedSeats[index]}
                                    </span>
                                  ) : (
                                    <span className="text-orange-600">غير محدد</span>
                                  )}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <UsersIcon className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                      <p>يرجى إضافة معلومات الشخص المسؤول أولاً</p>
                    </div>
                  )}
                </div>
              </FormCard>
            )}

            {/* Pricing */}
            <FormCard 
              title="التسعير" 
              icon={<CurrencyDollarIcon className="w-5 h-5 text-primary-600" />}
            >
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <FormField label="السعر الأساسي">
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.pricing.basePrice}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      pricing: { ...prev.pricing, basePrice: parseFloat(e.target.value) || 0 }
                    }))}
                  />
                </FormField>

                <FormField label="رسوم النقل">
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.pricing.transportationFee}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      pricing: { ...prev.pricing, transportationFee: parseFloat(e.target.value) || 0 }
                    }))}
                  />
                </FormField>

                <FormField label="رسوم الإقامة">
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.pricing.accommodationFee}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      pricing: { ...prev.pricing, accommodationFee: parseFloat(e.target.value) || 0 }
                    }))}
                  />
                </FormField>

                <FormField label="رسوم إضافية">
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.pricing.additionalFees}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      pricing: { ...prev.pricing, additionalFees: parseFloat(e.target.value) || 0 }
                    }))}
                  />
                </FormField>

                <FormField label="الخصم">
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.pricing.discount}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      pricing: { ...prev.pricing, discount: parseFloat(e.target.value) || 0 }
                    }))}
                  />
                </FormField>

                <FormField label="المبلغ المدفوع">
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.pricing.paidAmount}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      pricing: { ...prev.pricing, paidAmount: parseFloat(e.target.value) || 0 }
                    }))}
                  />
                </FormField>
              </div>

              <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                <div className="flex justify-between items-center text-lg font-semibold">
                  <span>المبلغ الإجمالي:</span>
                  <span className="text-primary-600">
                    {formatCurrency(formData.pricing.totalAmount)}
                  </span>
                </div>
                <div className="flex justify-between items-center text-sm text-gray-600 mt-1">
                  <span>المبلغ المتبقي:</span>
                  <span>
                    {formatCurrency(formData.pricing.totalAmount - formData.pricing.paidAmount)}
                  </span>
                </div>
                <div className="flex justify-between items-center text-sm text-gray-600 mt-1">
                  <span>عدد الأشخاص:</span>
                  <span>
                    {1 + formData.companions.length} شخص
                  </span>
                </div>
              </div>

              {errors['pricing.totalAmount'] && (
                <p className="text-red-600 text-sm mt-2">{errors['pricing.totalAmount']}</p>
              )}
            </FormCard>

            {/* Payment and Status */}
            <FormCard 
              title="الدفع والحالة" 
              icon={<CurrencyDollarIcon className="w-5 h-5 text-primary-600" />}
            >
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <FormField label="طريقة الدفع">
                  <Select
                    options={paymentMethodOptions}
                    value={formData.paymentMethod}
                    onChange={(e) => setFormData(prev => ({ ...prev, paymentMethod: e.target.value as any }))}
                  />
                </FormField>

                <FormField label="حالة الدفع">
                  <Select
                    options={paymentStatusOptions}
                    value={formData.paymentStatus}
                    onChange={(e) => setFormData(prev => ({ ...prev, paymentStatus: e.target.value as any }))}
                  />
                </FormField>

                <FormField label="حالة الحجز">
                  <Select
                    options={statusOptions}
                    value={formData.status}
                    onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value as any }))}
                  />
                </FormField>

                <FormField label="الأولوية">
                  <Select
                    options={priorityOptions}
                    value={formData.priority}
                    onChange={(e) => setFormData(prev => ({ ...prev, priority: e.target.value as any }))}
                  />
                </FormField>
              </div>
            </FormCard>

            {/* Special Requests */}
            <FormCard 
              title="طلبات خاصة" 
              icon={<DocumentTextIcon className="w-5 h-5 text-primary-600" />}
            >
              <FormField label="">
                <Textarea
                  rows={3}
                  placeholder="أي طلبات أو ملاحظات خاصة..."
                  value={formData.specialRequests}
                  onChange={(e) => setFormData(prev => ({ ...prev, specialRequests: e.target.value }))}
                />
              </FormField>
            </FormCard>

            {/* WhatsApp auto reminder */}
            <FormCard
              title="تذكير واتساب تلقائي"
              icon={<DocumentTextIcon className="w-5 h-5 text-primary-600" />}
            >
              <div className="space-y-4">
                <Checkbox
                  label="إرسال تذكير واتساب تلقائي لرقم التواصل قبل موعد الرحلة"
                  checked={formData.whatsappReminder?.enabled !== false}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      whatsappReminder: {
                        enabled: e.target.checked,
                        hoursBeforeDeparture:
                          prev.whatsappReminder?.hoursBeforeDeparture || 24,
                      },
                    }))
                  }
                />
                {formData.whatsappReminder?.enabled !== false && (
                  <FormField label="قبل الرحلة بـ (ساعة)">
                    <Input
                      type="number"
                      min={1}
                      max={720}
                      value={formData.whatsappReminder?.hoursBeforeDeparture ?? 24}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          whatsappReminder: {
                            enabled: true,
                            hoursBeforeDeparture: Math.max(
                              1,
                              Math.min(720, Number(e.target.value) || 24)
                            ),
                          },
                        }))
                      }
                    />
                  </FormField>
                )}
                <p className="text-xs text-gray-500">
                  مثال: 24 = يُرسل التذكير قبل مغادرة أول مسار بيوم. التأكيد يُرسل مباشرة عند إنشاء الحجز.
                </p>
              </div>
            </FormCard>

            {/* Submit Error */}
            {errors.submit && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-red-600">{errors.submit}</p>
              </div>
            )}

            {/* Form Actions */}
            <div className="flex justify-end space-x-3 space-x-reverse pt-6">
              <button
                type="button"
                onClick={onClose}
                className="btn-secondary"
                disabled={createMutation.isPending || updateMutation.isPending}
              >
                إلغاء
              </button>
              <button
                type="submit"
                className="btn-primary"
                disabled={createMutation.isPending || updateMutation.isPending}
              >
                {createMutation.isPending || updateMutation.isPending ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white ml-2"></div>
                    {isEditing ? 'جاري التحديث...' : 'جاري الحفظ...'}
                  </div>
                ) : (
                  isEditing ? 'تحديث الحجز' : 'حفظ الحجز'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default BookingForm; 